import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, FileText, Clock, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '../context/AuthContext';
import BrandingFooter from '@/components/BrandingFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HistoryPage() {
  const navigate = useNavigate();
  const [transcriptions, setTranscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchTranscriptions();
  }, []);

  const fetchTranscriptions = async () => {
    try {
      const response = await axios.get(`${API}/transcriptions`, {
        headers: getAuthHeaders()
      });
      setTranscriptions(response.data);
    } catch (error) {
      console.error('Error fetching transcriptions:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    
    try {
      await axios.delete(`${API}/transcription/${id}`, {
        headers: getAuthHeaders()
      });
      setTranscriptions(transcriptions.filter(t => t.id !== id));
      toast.success('Transcription deleted');
    } catch (error) {
      console.error('Error deleting transcription:', error);
      toast.error('Failed to delete transcription');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30',
      processing: 'bg-[#007AFF]/20 text-[#007AFF] border-[#007AFF]/30',
      failed: 'bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30'
    };
    return styles[status] || styles.processing;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative">
      <div className="hero-glow absolute top-0 left-0 right-0 h-[400px]" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8 fade-in">
          <Button
            data-testid="back-to-home-btn"
            onClick={() => navigate('/')}
            className="btn-secondary mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <h1 className="heading-font text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white mb-2">
            Transcription History
          </h1>
          <p className="text-[#A1A1AA]">View and manage your previous transcriptions</p>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-[#007AFF] mx-auto animate-spin mb-4" />
              <p className="text-[#A1A1AA]">Loading history...</p>
            </div>
          </div>
        ) : transcriptions.length === 0 ? (
          <div className="glass-card rounded-xl p-12 text-center">
            <FileText className="w-16 h-16 text-[#3F3F46] mx-auto mb-4" />
            <h2 className="heading-font text-2xl font-bold text-white uppercase mb-2">No Transcriptions Yet</h2>
            <p className="text-[#A1A1AA] mb-6">Upload your first video to get started</p>
            <Button
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Upload Video
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {transcriptions.map((transcription) => (
              <div
                key={transcription.id}
                data-testid={`history-item-${transcription.id}`}
                onClick={() => transcription.status === 'completed' && navigate(`/results/${transcription.id}`)}
                className={`glass-card rounded-xl p-6 transition-all duration-300 ${
                  transcription.status === 'completed' ? 'cursor-pointer card-hover' : 'cursor-default'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-[#007AFF]" />
                      <h3 className="text-white font-medium text-lg">{transcription.filename}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(transcription.status)}`}>
                        {transcription.status.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-[#A1A1AA]">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(transcription.created_at)}
                      </div>
                      {transcription.duration && (
                        <div>
                          Duration: {Math.floor(transcription.duration / 60)}:{String(Math.floor(transcription.duration % 60)).padStart(2, '0')}
                        </div>
                      )}
                      {transcription.language && (
                        <div className="uppercase">
                          {transcription.language}
                        </div>
                      )}
                    </div>

                    {transcription.error_message && (
                      <div className="mt-3 flex items-start gap-2 text-[#FF3B30] text-sm">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{transcription.error_message}</span>
                      </div>
                    )}
                  </div>

                  <Button
                    data-testid={`delete-btn-${transcription.id}`}
                    onClick={(e) => handleDelete(transcription.id, e)}
                    className="btn-secondary ml-4"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <BrandingFooter />
    </div>
  );
}